import React, { useState, useRef, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { ArrowUpIcon, SearchIcon, MicIcon, ImageIcon } from 'lucide-react';
import { generateGeminiContent } from '../services/usersapi';
import { useToast } from '../components/ui/use-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

const ResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { query, response } = location.state || {};
  
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Generate related questions based on the user's actual query
  const generateRelatedQuestions = (userQuery: string): string[] => {
    if (!userQuery) return [];
    
    const queryLower = userQuery.toLowerCase();
    const words = queryLower.split(' ');
    
    // Extract key topics from the query
    const keyWords = words.filter(word => 
      word.length > 3 && 
      !['what', 'how', 'when', 'where', 'why', 'which', 'the', 'and', 'for', 'with', 'about', 'from'].includes(word)
    );

    const relatedQuestions: string[] = [];

    // If the query contains specific topics, generate related questions
    if (keyWords.length > 0) {
      const mainTopic = keyWords[0];
      
      // Generate contextual questions based on query patterns
      if (queryLower.includes('how to') || queryLower.includes('how can')) {
        relatedQuestions.push(
          `What are the best practices for ${mainTopic}?`,
          `Common mistakes to avoid when ${mainTopic.replace('ing', '')}?`,
          `What tools are needed for ${mainTopic}?`,
          `Step by step guide to ${mainTopic}?`
        );
      } else if (queryLower.includes('what is') || queryLower.includes('what are')) {
        relatedQuestions.push(
          `How does ${mainTopic} work?`,
          `Benefits of ${mainTopic}?`,
          `${mainTopic} vs alternatives?`,
          `Where to learn more about ${mainTopic}?`
        );
      } else if (queryLower.includes('best') || queryLower.includes('top')) {
        relatedQuestions.push(
          `How to choose the right ${mainTopic}?`,
          `${mainTopic} for beginners?`,
          `Most popular ${mainTopic} options?`,
          `${mainTopic} comparison guide?`
        );
      } else {
        // Generic related questions based on main topic
        relatedQuestions.push(
          `How to get started with ${mainTopic}?`,
          `What are the benefits of ${mainTopic}?`,
          `${mainTopic} tips for beginners?`,
          `Common ${mainTopic} questions answered?`
        );
      }

      // Add more specific questions based on identified keywords
      keyWords.slice(1, 3).forEach(keyword => {
        relatedQuestions.push(
          `More about ${keyword} and ${mainTopic}?`,
          `${keyword} related to ${mainTopic}?`
        );
      });
    }

    // Fallback questions if no specific pattern is found
    if (relatedQuestions.length === 0) {
      relatedQuestions.push(
        `More information about "${userQuery}"?`,
        `Related topics to "${userQuery}"?`,
        `Advanced guide for "${userQuery}"?`,
        `Frequently asked questions about "${userQuery}"?`
      );
    }

    // Return only 4 unique questions
    return [...new Set(relatedQuestions)].slice(0, 4);
  };

  // Generate related queries based on the user's actual query - moved before early return
  const relatedQueries = useMemo(() => {
    return generateRelatedQuestions(query || '');
  }, [query]);

  // Convert response to string safely and extract clean text
  const getResponseText = (response: any): string => {
    if (typeof response === 'string') {
      return response;
    }
    if (response && typeof response === 'object') {
      if (response.candidates && Array.isArray(response.candidates)) {
        const candidate = response.candidates[0];
        if (candidate && candidate.content && candidate.content.parts && Array.isArray(candidate.content.parts)) {
          return candidate.content.parts.map((part: any) => part.text).join('\n\n');
        }
      }
      return response.content || 
             response.response || 
             response.text || 
             response.message ||
             response.data ||
             'No response available';
    }
    return 'No response available';
  };

  const responseText = useMemo(() => getResponseText(response), [response]);

  // Redirect to home if no data - moved after hooks
  if (!query || !response) {
    navigate('/');
    return null;
  }

  // Capitalize the first letter of the query
  const capitalizedQuery = query.charAt(0).toUpperCase() + query.slice(1);

  const handleNewSearch = async () => {
    if (inputValue.trim() && !isLoading) {
      setIsLoading(true);
      try {
        const result = await generateGeminiContent(inputValue.trim());
        navigate('/result', { 
          state: { 
            query: inputValue.trim(),
            response: result 
          },
          replace: true
        });
        setInputValue('');
        toast({
          title: "New response generated!",
          description: "Page updated with fresh results.",
        });
      } catch (error) {
        console.error('Error generating content:', error);
        toast({
          title: "Error",
          description: "Failed to generate response. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleNewSearch();
    }
  };

  const handleRelatedQuestionClick = async (question: string) => {
    setInputValue(question);
    setIsLoading(true);
    try {
      const result = await generateGeminiContent(question);
      navigate('/result', { 
        state: { 
          query: question,
          response: result 
        },
        replace: true
      });
      toast({
        title: "New response generated!",
        description: "Exploring related question.",
      });
    } catch (error) {
      console.error('Error generating content:', error);
      setInputValue(question);
      inputRef.current?.focus();
      toast({
        title: "Error",
        description: "Failed to generate response. Question added to search box.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Main Content */}
      <main className="container mx-auto px-6 py-8 w-full max-w-6xl overflow-y-auto pb-28">
        {/* Query Display */}
        <section className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{capitalizedQuery}</h1>
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <SearchIcon className="w-4 h-4" />
            <span>AI-generated response</span>
          </div>
        </section>

        {/* Response Content */}
        <section className="mb-8 bg-white rounded-lg shadow-sm p-6">
          <div className="prose prose-md max-w-none text-gray-700 leading-relaxed">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-4">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 mb-4">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-4">{children}</ol>,
                li: ({ children }) => <li className="mb-2">{children}</li>,
                h1: ({ children }) => <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-bold text-gray-900 mb-3">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-bold text-gray-900 mb-2">{children}</h3>,
              }}
            >
              {responseText}
            </ReactMarkdown>
          </div>
        </section>

        {/* Related Questions */}
        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">People also ask</h3>
          <div className="space-y-2">
            {relatedQueries.map((question, index) => (
              <button
                key={index}
                onClick={() => handleRelatedQuestionClick(question)}
                disabled={isLoading}
                className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-2">
                  <SearchIcon className="w-4 h-4 text-gray-500 hover:text-blue-600" />
                  <span>{question}</span>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Sources */}
        <section className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Sources</h3>
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              <span className="text-gray-600">AI-generated content</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              <span className="text-gray-600">Knowledge base</span>
            </div>
          </div>
        </section>
      </main>

      {/* Bottom Fixed Search Bar */}
      <footer className="fixed bottom-6 left-0 right-0 z-50 flex justify-center">
        <div className="w-full max-w-5xl px-6">
          <div className="flex items-center gap-4 border border-gray-300 rounded-full bg-white shadow-2xl p-4 transition-shadow hover:shadow-3xl " style={{marginLeft: "64px"}}>
            <SearchIcon className="w-6 h-6 text-gray-500" />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isLoading ? "Generating new response..." : "Search"}
              disabled={isLoading}
              className="flex-1 border-none outline-none text-gray-900 placeholder:text-gray-400 text-lg py-2 bg-transparent disabled:opacity-50"
            />
            <div className="flex items-center gap-3">
              {/* <Button
                variant="ghost"
                size="sm"
                className="p-2 h-auto text-gray-600 hover:text-gray-900 rounded-full"
              >
                <MicIcon className="w-6 h-6" />
              </Button> */}
              {/* <Button
                variant="ghost"
                size="sm"
                className="p-2 h-auto text-gray-600 hover:text-gray-900 rounded-full"
              >
                <ImageIcon className="w-6 h-6" />
              </Button> */}
              <Button
                onClick={handleNewSearch}
                size="sm"
                className="p-3 h-auto bg-orange-600 hover:bg-orange-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!inputValue.trim() || isLoading}
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SearchIcon className="w-6 h-6 text-white" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResultPage;